import { match } from 'ts-pattern';
import type { AssertionMethod } from '~/ai_tools/playwright_utils/types';
import { actionMethodsSet, assertionMethodsSet, type ActionMethod } from '~/ai_tools/playwright_utils/types';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { OperationData } from './types';

export const opModelToSelectedOp = (
  input: OperationData,
  options?: {
    traceZipUrl?: string;
  },
): SelectedOperation => {
  const operationField = match(input.operation)
    .with({ operationType: 'ACTION' }, ({ action, value, valueType, target, target2, options }) => ({
      actionOp: {
        action: action as string,
        value: value || null,
        valueType: valueType || null,
        targetV2:
          target ?
            {
              name: target.name || '',
              locator: target.locator || '',
              boundingBox: target.boundingBox || undefined,
            }
          : null,
        target2V2:
          target2 ?
            {
              name: target2.name || '',
              locator: target2.locator || '',
              boundingBox: target2.boundingBox || undefined,
            }
          : null,
        options: options || null,
      } satisfies SelectedOperation['actionOp'],
    }))
    .with({ operationType: 'ASSERTION' }, ({ assertion, invert, value, valueType, target, options }) => ({
      assertionOp: {
        assertion: assertion as string,
        invert: invert || null,
        value: value || null,
        valueType: valueType || null,
        targetV2:
          target ?
            {
              name: target.name || '',
              locator: target.locator || '',
              boundingBox: target.boundingBox || undefined,
            }
          : null,
        options: options || null,
      } satisfies SelectedOperation['assertionOp'],
    }))
    .with({ operationType: 'AI_ASSERTION' }, ({ assertion, screenshotType, includeScreenshot, model }) => ({
      aiAssertionOp: {
        assertion,
        screenshotType,
        includeScreenshot: includeScreenshot ?? true,
        model: model ?? 'STABLY_FLASH',
      } satisfies SelectedOperation['aiAssertionOp'],
    }))
    .with({ operationType: 'AI_ACTION' }, ({ prompt, model }) => ({
      aiActionOp: {
        prompt,
        model: model ?? 'STABLY_FLASH',
      } satisfies SelectedOperation['aiActionOp'],
    }))
    .with({ operationType: 'NAVIGATION' }, ({ value, valueType, newPage, options }) => ({
      navigationOp: {
        value,
        valueType: valueType || 'STATIC',
        newPage: newPage || false,
        options: options || null,
      } satisfies SelectedOperation['navigationOp'],
    }))
    .with({ operationType: 'WAIT' }, ({ timeoutInMillis }) => ({
      waitOp: {
        timeoutInMillis,
      } satisfies SelectedOperation['waitOp'],
    }))
    .with({ operationType: 'CODE_BLOCK' }, ({ code, description }) => ({
      codeBlockOp: {
        code,
        description: description || null,
      } satisfies SelectedOperation['codeBlockOp'],
    }))
    .with({ operationType: 'UPLOAD' }, ({ files }) => ({
      uploadOp: {
        uploadedFiles: files,
      } satisfies SelectedOperation['uploadOp'],
    }))
    .with(
      { operationType: 'DEFINE_VARIABLE' },
      ({ name, value, type, includeScreenshot, extractionModel, extractionMethod }) => {
        // For legacy data with STABLY_DEEP_THINK and no extraction method,
        // default to SCREENSHOT instead of ARIA_SNAPSHOT
        const finalExtractionMethod =
          extractionMethod ?? (extractionModel === 'STABLY_DEEP_THINK' ? 'SCREENSHOT' : 'ARIA_SNAPSHOT');

        return {
          defineVariableOp: {
            name,
            value,
            type,
            includeScreenshot: includeScreenshot ?? true,
            extractionModel: extractionModel ?? 'STABLY_FLASH',
            extractionMethod: finalExtractionMethod,
          },
        };
      },
    )
    .with(
      { operationType: 'RECEIVE_EMAIL' },
      ({ toEmailAddressValue, toEmailAddressType, variableExtraction, filters }) => ({
        receiveEmailOp: {
          toEmailAddressValue,
          toEmailAddressType,
          variableExtraction: variableExtraction || null,
          filters: filters || null,
        } satisfies SelectedOperation['receiveEmailOp'],
      }),
    )
    .with({ operationType: 'FETCH_REQUEST' }, ({ responseVariableName, request: { url, method, headers, body } }) => ({
      fetchRequestOp: {
        responseVariableName: responseVariableName || null,
        url,
        method,
        headers: headers || null,
        body: body || null,
      } satisfies SelectedOperation['fetchRequestOp'],
    }))
    .with({ operationType: 'AUTH_STATE' }, ({ actionType, testAccountId }) => ({
      authStateOp: {
        testAccountId,
        actionType,
      } satisfies SelectedOperation['authStateOp'],
    }))
    .with({ operationType: 'COMPONENT_CALL' }, ({ componentId, componentName, inputParameters }) => ({
      componentCallOp: {
        componentId,
        inputParameters: inputParameters || null,
        // Setting this to null/empty values is a bit of a hack
        // We do this becasue this information is a view into the component
        component: {
          name: componentName || 'Component',
          parameters: null,
          archived: false,
          outputVariables: null,
        },
      } satisfies SelectedOperation['componentCallOp'],
    }))
    .with({ operationType: 'SPECIAL_LOGIN' }, ({ type, testAccountId }) => ({
      specialLoginOp: {
        type,
        testAccountId,
      } satisfies SelectedOperation['specialLoginOp'],
    }))
    .with({ operationType: 'CONDITIONAL' }, ({ conditionPrompt }) => ({
      conditionalOp: {
        conditionPrompt,
        // Empty placeholder id for the conditional block, also a bit of a hack
        conditionalBlockId: '',
      } satisfies SelectedOperation['conditionalOp'],
    }))
    .with({ operationType: 'END_TEST' }, ({ passTest }) => ({
      endTestOp: {
        passTest,
      } satisfies SelectedOperation['endTestOp'],
    }))
    .exhaustive();

  return {
    ...(options?.traceZipUrl !== undefined && { traceZipUrl: options.traceZipUrl }),
    id: input.operationId || '',
    pageAlias: input.pageAlias || 'page',
    opensPageAlias: input.opensPageAlias || null,
    opensFileUploadDialog: input.opensFileUploadDialog || null,
    order: input.order || 0,
    type: input.operation.operationType,
    beforeScreenshot: null,
    actionScreenshot: null,
    afterScreenshot: null,
    testStep: input.stepId ? { id: input.stepId } : null,
    skip: input.skip || false,
    // To make this work, we first set all the operations to null and then just override the one we want to set
    actionOp: null,
    assertionOp: null,
    aiActionOp: null,
    aiAssertionOp: null,
    navigationOp: null,
    waitOp: null,
    codeBlockOp: null,
    uploadOp: null,
    defineVariableOp: null,
    receiveEmailOp: null,
    fetchRequestOp: null,
    authStateOp: null,
    specialLoginOp: null,
    componentCallOp: null,
    conditionalOp: null,
    endTestOp: null,
    ...operationField,
  };
};

export const selectedOpToOpModel = (input: SelectedOperation): OperationData & { pageAlias: string } => {
  // Handle different operation types
  const operation =
    input.actionOp && input.type === 'ACTION' && actionMethodsSet.has(input.actionOp.action as ActionMethod) ?
      ({
        operationType: 'ACTION',
        action: input.actionOp.action as ActionMethod,
        value: input.actionOp.value || undefined,
        valueType: input.actionOp.valueType || undefined,
        target: input.actionOp.targetV2 || undefined,
        target2: input.actionOp.target2V2 || undefined,
        options: input.actionOp.options || undefined,
      } as const)
    : (
      input.assertionOp &&
      input.type === 'ASSERTION' &&
      assertionMethodsSet.has(input.assertionOp.assertion as AssertionMethod)
    ) ?
      ({
        operationType: 'ASSERTION',
        assertion: input.assertionOp.assertion as AssertionMethod,
        invert: input.assertionOp.invert || undefined,
        value: input.assertionOp.value || undefined,
        valueType: input.assertionOp.valueType || undefined,
        target: input.assertionOp.targetV2 || undefined,
        options: input.assertionOp.options || undefined,
      } as const)
    : input.aiAssertionOp && input.type === 'AI_ASSERTION' ?
      ({
        operationType: 'AI_ASSERTION',
        assertion: input.aiAssertionOp.assertion,
        screenshotType: input.aiAssertionOp.screenshotType,
        includeScreenshot: input.aiAssertionOp.includeScreenshot,
        model: input.aiAssertionOp.model,
      } as const)
    : input.aiActionOp && input.type === 'AI_ACTION' ?
      ({
        operationType: 'AI_ACTION',
        prompt: input.aiActionOp.prompt,
        model: input.aiActionOp.model,
      } as const)
    : input.navigationOp && input.type === 'NAVIGATION' ?
      ({
        operationType: 'NAVIGATION',
        value: input.navigationOp.value,
        valueType: input.navigationOp.valueType || 'STATIC',
        newPage: input.navigationOp.newPage || false,
        options: input.navigationOp.options || undefined,
      } as const)
    : input.waitOp && input.type === 'WAIT' ?
      ({
        operationType: 'WAIT',
        timeoutInMillis: input.waitOp.timeoutInMillis,
      } as const)
    : input.codeBlockOp && input.type === 'CODE_BLOCK' ?
      ({
        operationType: 'CODE_BLOCK',
        code: input.codeBlockOp.code,
        description: input.codeBlockOp.description || undefined,
      } as const)
    : input.uploadOp && input.type === 'UPLOAD' ?
      ({
        operationType: 'UPLOAD',
        files: input.uploadOp.uploadedFiles,
      } as const)
    : input.defineVariableOp && input.type === 'DEFINE_VARIABLE' ?
      ({
        operationType: 'DEFINE_VARIABLE',
        name: input.defineVariableOp.name,
        value: input.defineVariableOp.value,
        type: input.defineVariableOp.type,
        includeScreenshot: input.defineVariableOp.includeScreenshot,
        extractionModel: input.defineVariableOp.extractionModel,
        extractionMethod:
          input.defineVariableOp.extractionMethod ||
          (input.defineVariableOp.extractionModel === 'STABLY_DEEP_THINK' ? 'SCREENSHOT' : 'ARIA_SNAPSHOT'),
      } as const)
    : input.receiveEmailOp && input.type === 'RECEIVE_EMAIL' ?
      ({
        operationType: 'RECEIVE_EMAIL',
        toEmailAddressValue: input.receiveEmailOp.toEmailAddressValue,
        toEmailAddressType: input.receiveEmailOp.toEmailAddressType,
        variableExtraction: input.receiveEmailOp.variableExtraction || undefined,
        filters: input.receiveEmailOp.filters || undefined,
      } as const)
    : input.fetchRequestOp && input.type === 'FETCH_REQUEST' ?
      ({
        operationType: 'FETCH_REQUEST',
        responseVariableName: input.fetchRequestOp.responseVariableName || undefined,
        request: {
          url: input.fetchRequestOp.url,
          method: input.fetchRequestOp.method,
          headers: input.fetchRequestOp.headers || undefined,
          body: input.fetchRequestOp.body || undefined,
        },
      } as const)
    : input.authStateOp && input.type === 'AUTH_STATE' ?
      ({
        operationType: 'AUTH_STATE',
        actionType: input.authStateOp.actionType,
        testAccountId: input.authStateOp.testAccountId,
      } as const)
    : input.componentCallOp && input.type === 'COMPONENT_CALL' ?
      ({
        operationType: 'COMPONENT_CALL',
        componentId: input.componentCallOp.componentId,
        componentName: input.componentCallOp.component?.name || undefined,
        componentOutputVariables: input.componentCallOp.component?.outputVariables || undefined,
        inputParameters: input.componentCallOp.inputParameters || undefined,
      } as const)
    : input.specialLoginOp && input.type === 'SPECIAL_LOGIN' ?
      ({
        operationType: 'SPECIAL_LOGIN',
        type: input.specialLoginOp.type,
        testAccountId: input.specialLoginOp.testAccountId,
      } as const)
    : input.conditionalOp && input.type === 'CONDITIONAL' ?
      ({
        operationType: 'CONDITIONAL',
        conditionPrompt: input.conditionalOp.conditionPrompt,
        conditionalBlockId: input.conditionalOp.conditionalBlockId,
      } as const)
    : input.endTestOp && input.type === 'END_TEST' ?
      ({
        operationType: 'END_TEST',
        passTest: input.endTestOp.passTest,
      } as const)
    : undefined;
  if (!operation) {
    throw new Error(`Unable to convert operation: ${input}`);
  }

  // Create a basic operation structure
  return {
    operation,
    operationId: input.id,
    pageAlias: input.pageAlias || 'page',
    opensPageAlias: input.opensPageAlias || undefined,
    opensFileUploadDialog: input.opensFileUploadDialog || undefined,
    order: input.order || 0,
    stepId: input.testStep?.id || undefined,
    skip: input.skip || false,
  };
};
